import { Clock, LogIn } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button,
} from '@parsina/framework';
import { C, soft, RADIUS } from '@parsina/framework/clinical/src/constants/theme';
import { type } from '@parsina/framework/clinical/src/constants/typography';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

// ─────────────────────────────────────────────────────────────────────────────
// The two faces of a session ending.
//
// Both are modal — genuinely modal, unlike almost anything else in this app —
// because unlike a connection drop there is nothing useful the doctor can do
// underneath. Every write will fail, and letting them keep typing into a
// clinical note that cannot be saved is worse than interrupting them.
//
// What the modal deliberately does NOT do is unmount anything. The screen
// behind it is intact and readable, which is the entire point: the previous
// behaviour navigated away, so the note was simply gone. Here it is still on
// screen, still selectable, and after «ورود مجدد» the doctor comes back to the
// same URL.
//
//   Warning  raised while the session can still be extended silently. It has a
//            live countdown, because "soon" is not something anyone can plan
//            around, and its primary action costs one silent token refresh.
//            Dismissible — a doctor mid-sentence may reasonably want it gone,
//            and the expired dialog will follow if they do nothing.
//
//   Expired  not dismissible. There is no state to return to underneath in
//            which anything works.
//
// Built on the kit `Dialog` (Radix) rather than a hand-rolled fixed overlay.
// That is not tidiness: it is what buys the focus trap, the `aria-modal`
// semantics, the escape/outside-click handling, the scroll lock and the scrim
// that every other modal in the app already has. A bespoke overlay here would
// be the one dialog in the product that a keyboard user could tab out of, and
// «ورود مجدد» is a bad button to lose focus of.
// ─────────────────────────────────────────────────────────────────────────────

/** The tone disc, matching SystemPage's and ErrorState's so the family reads as one. */
function Disc({ tone, children }) {
  const palette = tone === 'warning'
    ? { fg: C.amber, bg: C.amberLight }
    : { fg: C.navy, bg: soft(C.navy, 0.1) };
  return (
    <div style={{
      width: 56, height: 56, borderRadius: RADIUS.pill,
      background: palette.bg, color: palette.fg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 1rem',
    }}>
      {children}
    </div>
  );
}

/**
 * Session about to end.
 *
 * @param {number} secondsLeft  live countdown, owned by the caller
 * @param {Function} onExtend   silently renews the token
 * @param {Function} onDismiss  hides this; the expired dialog still follows
 * @param {boolean} busy        a renewal is in flight
 */
export function SessionExpiringDialog({ secondsLeft, onExtend, onDismiss, busy }) {
  const { t, formatNumber } = useTranslation();

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onDismiss?.(); }}>
      {/* `alertdialog`, not Radix's default `dialog`: this interrupts to
          announce a time-limited condition that needs a response, which is
          exactly the distinction the two roles draw. A screen reader announces
          an alertdialog's content immediately on open — for a countdown the
          doctor cannot see, that is the whole message. */}
      <DialogContent role="alertdialog" data-testid="session-expiring" showClose={false}>
        <DialogHeader>
          <Disc tone="warning"><Clock size={26} strokeWidth={1.9} aria-hidden /></Disc>
          <DialogTitle style={{ textAlign: 'center' }}>
            {t('auth.session.expiringTitle')}
          </DialogTitle>
        </DialogHeader>

        <div style={{ textAlign: 'center' }}>
          <div style={{ ...type.body, fontWeight: 500, color: C.textSecondary, lineHeight: 1.85 }}>
            {t('auth.session.expiringBody')}
          </div>

          {/* The countdown is the reason this dialog works. «به‌زودی» gives a
              doctor nothing to decide with; a number they can watch does. */}
          <div
            data-testid="session-countdown"
            aria-live="off"
            style={{ ...type.display, color: C.amber, margin: '0.5rem 0 0', fontVariantNumeric: 'tabular-nums' }}
          >
            {formatNumber(Math.max(0, secondsLeft))}
            <span style={{ ...type.unit, color: C.textMuted, marginInlineStart: '0.375rem' }}>
              {t('common.units.second')}
            </span>
          </div>
        </div>

        {/* Column, not the footer's default row: the two actions here are not
            peers. Extending is what the doctor almost always wants; dismissing
            is an escape hatch and reads as one at `ghost`. */}
        <DialogFooter style={{ flexDirection: 'column', gap: '0.5rem' }}>
          <Button onClick={onExtend} disabled={busy} data-testid="session-extend" style={{ width: '100%' }}>
            {t(busy ? 'common.state.pleaseWait' : 'auth.session.staySignedIn')}
          </Button>
          <Button variant="ghost" onClick={onDismiss} data-testid="session-dismiss" style={{ width: '100%' }}>
            {t('common.actions.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Session over. One way out, and it keeps the doctor's place.
 */
export function SessionExpiredDialog({ onSignIn, busy }) {
  const { t } = useTranslation();

  return (
    // Not dismissible, and it takes three separate refusals to mean it: no
    // close affordance, no escape, no outside click. Any one of them left open
    // would drop the doctor back onto a screen where every button silently
    // fails — which looks exactly like the app having frozen.
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        role="alertdialog"
        data-testid="session-expired"
        showClose={false}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <Disc tone="info"><LogIn size={26} strokeWidth={1.9} aria-hidden /></Disc>
          <DialogTitle style={{ textAlign: 'center' }}>
            {t('errors.kind.unauthenticated.title')}
          </DialogTitle>
        </DialogHeader>

        <div style={{ ...type.body, fontWeight: 500, color: C.textSecondary, lineHeight: 1.85, textAlign: 'center' }}>
          {t('auth.session.expiredBody')}
        </div>

        <DialogFooter style={{ flexDirection: 'column', gap: '0.5rem' }}>
          <Button onClick={onSignIn} disabled={busy} data-testid="session-signin" style={{ width: '100%' }}>
            {t('auth.session.signInAgain')}
          </Button>
        </DialogFooter>

        {/* Said plainly, because it is the first thing a doctor looking at a
            half-written note will want to know, and the honest answer is «what
            you saved is safe, what is on screen is not saved». */}
        <div style={{ ...type.caption, color: C.textMuted, marginTop: '1rem', lineHeight: 1.8, textAlign: 'center' }}>
          {t('auth.session.returnNotice')}
        </div>
      </DialogContent>
    </Dialog>
  );
}
